import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText,
  Upload,
  Trash2,
  Plus,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Package,
  Eye,
  ExternalLink,
  Loader2,
  AlertCircle,
  File,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MoneyInput } from '../ui/money-input';
import { AtLimit } from '../ui/char-counter';
import InlineDatePicker from '../InlineDatePicker';
import { supplierInvoiceService } from '../../services/supplierInvoiceService';
import { localDocumentStore, LocalDocument } from '../../services/localDocumentStore';
import { Supplier, SupplierInvoiceResponse } from '../../types';
import { formatMoney } from '../../utils/format';
import { showError } from '../Alerts';
import { handleDeleteError } from '../../utils/errorHandler';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderRef {
  id: string;
  orderNumber: string;
  supplierName: string;
  supplierCuit: string;
  total: number;
  /** Neto de la OC — se usa para sugerir el subtotal al cargar una factura */
  subtotal?: number;
  /** IVA de la OC — se usa para sugerir el % de IVA al cargar una factura */
  ivaAmount?: number;
  currency: 'ARS' | 'USD';
  date: string;
}

interface DocumentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OrderRef | null;
  suppliers: Supplier[];
  /** Called when the invoice count changes (created or deleted) to refresh the facturas badge */
  onInvoiceCountChange?: (orderId: string) => void;
  /** Called when the remito count changes for this order */
  onRemitoCountChange?: (orderId: string, count: number) => void;
}

type ActiveTab = 'facturas' | 'remitos';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getNumericDisplayValue = (value: number) =>
  value === 0 ? '' : value.toString();

const IVA_OPTIONS = [
  { value: 0, label: '0%' },
  { value: 10.5, label: '10,5%' },
  { value: 21, label: '21%' },
  { value: 27, label: '27%' },
];

const DEFAULT_IVA_PERCENT = 21;

/**
 * Sugiere el % de IVA a partir de los montos de la OC, ajustado a una de las
 * opciones válidas. Si la OC no tiene IVA (tipo B / monotributista) → 0%.
 */
const suggestIvaPercent = (subtotal?: number, ivaAmount?: number): number => {
  if (subtotal == null || subtotal <= 0) return DEFAULT_IVA_PERCENT;
  if (ivaAmount == null || ivaAmount <= 0) return 0;
  const raw = (ivaAmount / subtotal) * 100;
  return IVA_OPTIONS.reduce(
    (closest, opt) =>
      Math.abs(opt.value - raw) < Math.abs(closest - raw) ? opt.value : closest,
    DEFAULT_IVA_PERCENT,
  );
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCEPT = '.pdf,.jpg,.jpeg,.png';
const MAX_SIZE_MB = 10;
const VALID_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

function validateFile(file: File): string | null {
  if (!VALID_TYPES.includes(file.type)) return 'Solo se permiten archivos PDF, JPG o PNG';
  if (file.size > MAX_SIZE_MB * 1024 * 1024) return `El archivo no debe superar los ${MAX_SIZE_MB} MB`;
  return null;
}

function fileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />;
  return <File className="w-4 h-4 text-blue-500 flex-shrink-0" />;
}

function PaymentBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; cls: string }> = {
    PENDIENTE: { label: 'Pendiente', cls: 'bg-warning/10 text-warning' },
    PARCIALMENTE_PAGADA: { label: 'Parcial', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    PAGADA: { label: 'Pagada', cls: 'bg-success/10 text-success' },
  };
  const { label, cls } = cfg[status] ?? cfg.PENDIENTE;
  return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>{label}</span>;
}

// ─── PDF Viewer sub-dialog ────────────────────────────────────────────────────

interface PdfViewerProps {
  url: string | null;
  title: string;
  mimeType: string;
  onClose: () => void;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ url, title, mimeType, onClose }) => {
  if (!url) return null;
  const isImage = mimeType.startsWith('image/');
  return (
    <Dialog open={!!url} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="flex-shrink-0 px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="flex items-center gap-2 text-sm font-semibold truncate">
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              {title}
            </DialogTitle>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="w-3 h-3 mr-1.5" />
                Abrir en pestaña
              </Button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <DialogDescription className="sr-only">Vista previa de {title}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-muted/30">
          {isImage ? (
            <img
              src={url}
              alt={title}
              className="w-full h-full object-contain"
            />
          ) : (
            <iframe
              src={url}
              title={title}
              className="w-full h-full border-0"
              allow="fullscreen"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Drop zone ────────────────────────────────────────────────────────────────

interface DropZoneProps {
  onFileSelected: (file: File) => void;
  pending: File | null;
  onClear: () => void;
  label: string;
}

const DropZone: React.FC<DropZoneProps> = ({ onFileSelected, pending, onClear, label }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { showError(err); return; }
    onFileSelected(file);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`relative flex flex-col items-center justify-center gap-2 p-5 border-2 border-dashed rounded-xl transition-all cursor-pointer
        ${dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
      onClick={() => !pending && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const err = validateFile(file);
          if (err) { showError(err); return; }
          onFileSelected(file);
          e.target.value = '';
        }}
      />
      {pending ? (
        <div className="flex items-center gap-3 w-full">
          {fileIcon(pending.type)}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{pending.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(pending.size)}</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="p-1 rounded hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <>
          <Upload className="w-6 h-6 text-muted-foreground/60" />
          <p className="text-sm text-muted-foreground text-center">
            {label}
          </p>
          <p className="text-xs text-muted-foreground/60">PDF, JPG, PNG — máx {MAX_SIZE_MB} MB</p>
        </>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DocumentsDialog: React.FC<DocumentsDialogProps> = ({
  open,
  onOpenChange,
  order,
  suppliers,
  onInvoiceCountChange,
  onRemitoCountChange,
}) => {
  // ── Tab ──
  const [tab, setTab] = useState<ActiveTab>('facturas');

  // ── Facturas ──
  const [invoices, setInvoices] = useState<SupplierInvoiceResponse[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [invoiceSuccess, setInvoiceSuccess] = useState('');
  const [form, setForm] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    currency: 'ARS' as 'ARS' | 'USD',
    subtotal: 0,
    ivaPercent: 21,
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // ── Remitos ──
  const [remitos, setRemitos] = useState<LocalDocument[]>([]);
  const [loadingRemitos, setLoadingRemitos] = useState(false);
  const [remitoFile, setRemitoFile] = useState<File | null>(null);
  const [savingRemito, setSavingRemito] = useState(false);

  // ── PDF Viewer ──
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerMime, setViewerMime] = useState('application/pdf');

  // ── Load data on open ──────────────────────────────────────────────────────

  const loadInvoices = useCallback(async (orderId: string) => {
    setLoadingInvoices(true);
    try {
      const data = await supplierInvoiceService.getInvoicesByPurchaseOrder(Number(orderId));
      setInvoices(data);
    } catch {
      showError('Error al cargar facturas vinculadas');
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  const loadRemitos = useCallback(async (orderId: string) => {
    setLoadingRemitos(true);
    try {
      const data = await localDocumentStore.getByOrder(orderId);
      setRemitos(data.filter((d) => d.type === 'remito'));
    } catch {
      // Non-critical
    } finally {
      setLoadingRemitos(false);
    }
  }, []);

  useEffect(() => {
    if (open && order) {
      setTab('facturas');
      setShowForm(false);
      setExpandedId(null);
      setInvoiceFile(null);
      setRemitoFile(null);
      setInvoiceSuccess('');
      setFormErrors([]);
      resetForm();
      loadInvoices(order.id);
      loadRemitos(order.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id]);

  // ── PDF viewer ─────────────────────────────────────────────────────────────

  const openViewer = async (fileUrl: string, fileName: string, mime: string) => {
    let blobUrl: string | null = null;

    if (fileUrl.startsWith('local://')) {
      const docId = fileUrl.replace('local://', '');
      blobUrl = await localDocumentStore.createObjectUrl(docId);
      if (!blobUrl) {
        showError('El archivo no está disponible localmente. Puede haber sido eliminado o cargado en otro navegador.');
        return;
      }
    } else if (fileUrl.startsWith('http')) {
      blobUrl = fileUrl;
    } else {
      showError('URL de archivo no reconocida');
      return;
    }

    setViewerTitle(fileName);
    setViewerMime(mime);
    setViewerUrl(blobUrl);
  };

  const closeViewer = () => {
    if (viewerUrl && viewerUrl.startsWith('blob:')) URL.revokeObjectURL(viewerUrl);
    setViewerUrl(null);
    setViewerTitle('');
    setViewerMime('application/pdf');
  };

  const openRemitoViewer = async (doc: LocalDocument) => {
    const blob = await localDocumentStore.getBlob(doc.id);
    if (!blob) { showError('Archivo no disponible'); return; }
    const url = URL.createObjectURL(blob);
    setViewerTitle(doc.fileName);
    setViewerMime(doc.mimeType);
    setViewerUrl(url);
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  // Prellenar con los montos de la OC como sugerencia (editable).
  const resetForm = () => {
    setForm({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      currency: order?.currency ?? 'ARS',
      subtotal: order?.subtotal ?? 0,
      ivaPercent: suggestIvaPercent(order?.subtotal, order?.ivaAmount),
      notes: '',
    });
    setFormErrors([]);
    setInvoiceFile(null);
  };

  const currSymbol = (currency: 'ARS' | 'USD') => (currency === 'USD' ? 'USD' : '$');

  // Control de sobre-facturación: la suma de facturas de la OC no puede superar su total
  // (con una tolerancia). Facturar de menos está permitido (parciales).
  const ocInvoicing = (() => {
    if (!order) return null;
    const ivaTotal = Math.round((form.subtotal * form.ivaPercent) / 100 * 100) / 100;
    const newTotal = Math.round((form.subtotal + ivaTotal) * 100) / 100;
    const ocTotal = order.total ?? 0;
    const alreadyInvoiced = invoices
      .filter((i) => i.currency === form.currency)
      .reduce((s, i) => s + i.totalAmount, 0);
    const available = Math.max(0, ocTotal - alreadyInvoiced);
    const tolerance = Math.max(1, ocTotal * 0.01);
    const sameCurrency = form.currency === order.currency;
    const exceeds = sameCurrency && ocTotal > 0 && newTotal - available > tolerance;
    return { newTotal, ocTotal, alreadyInvoiced, available, exceeds, sameCurrency };
  })();

  // ── Handlers: Facturas ─────────────────────────────────────────────────────

  const handleSaveInvoice = async () => {
    if (!order) return;
    const errors: string[] = [];
    if (!form.invoiceNumber.trim()) errors.push('El número de factura es obligatorio');
    if (!form.invoiceDate) errors.push('La fecha es obligatoria');
    if (form.subtotal <= 0) errors.push('El subtotal debe ser mayor a 0');
    if (ocInvoicing?.exceeds) {
      errors.push(
        `El total de la factura supera lo disponible para facturar de la OC (${currSymbol(form.currency)} ${formatMoney(ocInvoicing.available)}).`,
      );
    }
    if (errors.length > 0) { setFormErrors(errors); return; }

    const supplier = suppliers.find(
      (s) => s.businessName === order.supplierName || s.cuit === order.supplierCuit,
    );
    if (!supplier) {
      setFormErrors(['No se encontró el proveedor asociado a esta orden']);
      return;
    }

    const ivaTotal = Math.round(form.subtotal * form.ivaPercent / 100 * 100) / 100;
    const totalAmount = Math.round((form.subtotal + ivaTotal) * 100) / 100;

    setSavingInvoice(true);
    setFormErrors([]);
    try {
      let fileUrl: string | undefined;
      if (invoiceFile) {
        const docId = await localDocumentStore.save({
          orderId: order.id,
          type: 'invoice_file',
          fileName: invoiceFile.name,
          fileSize: invoiceFile.size,
          mimeType: invoiceFile.type,
          uploadedAt: new Date().toISOString(),
          blob: invoiceFile,
        });
        fileUrl = `local://${docId}`;
      }

      await supplierInvoiceService.createInvoice({
        supplierId: supplier.id,
        purchaseOrderId: Number(order.id),
        invoiceNumber: form.invoiceNumber,
        invoiceDate: form.invoiceDate,
        currency: form.currency,
        subtotal: form.subtotal,
        ivaTotal,
        totalAmount,
        fileUrl,
        notes: form.notes || undefined,
      });

      await loadInvoices(order.id);
      onInvoiceCountChange?.(order.id);
      setInvoiceSuccess('Factura registrada exitosamente');
      setShowForm(false);
      resetForm();
      setTimeout(() => setInvoiceSuccess(''), 4000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Error al registrar la factura';
      setFormErrors([msg]);
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleDeleteInvoice = async (inv: SupplierInvoiceResponse) => {
    if (!order) return;
    setDeletingId(inv.id);
    try {
      await supplierInvoiceService.deleteInvoice(inv.id);
      setConfirmDeleteId(null);
      await loadInvoices(order.id);
      onInvoiceCountChange?.(order.id);
      setInvoiceSuccess('Factura eliminada');
      setTimeout(() => setInvoiceSuccess(''), 4000);
    } catch (e) {
      handleDeleteError(e, 'la factura');
    } finally {
      setDeletingId(null);
    }
  };

  // ── Handlers: Remitos ──────────────────────────────────────────────────────

  const handleSaveRemito = async () => {
    if (!order || !remitoFile) return;
    setSavingRemito(true);
    try {
      await localDocumentStore.save({
        orderId: order.id,
        type: 'remito',
        fileName: remitoFile.name,
        fileSize: remitoFile.size,
        mimeType: remitoFile.type,
        uploadedAt: new Date().toISOString(),
        blob: remitoFile,
      });
      setRemitoFile(null);
      await loadRemitos(order.id);
      const newCount = remitos.length + 1;
      onRemitoCountChange?.(order.id, newCount);
    } catch {
      showError('Error al guardar el remito');
    } finally {
      setSavingRemito(false);
    }
  };

  const handleDeleteRemito = async (docId: string) => {
    if (!order) return;
    try {
      await localDocumentStore.delete(docId);
      const updated = remitos.filter((r) => r.id !== docId);
      setRemitos(updated);
      onRemitoCountChange?.(order.id, updated.length);
    } catch {
      showError('Error al eliminar el remito');
    }
  };

  // ── Close ──────────────────────────────────────────────────────────────────

  const handleClose = () => {
    closeViewer();
    onOpenChange(false);
  };

  if (!order) return null;

  const curr = currSymbol(order.currency);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] flex flex-col gap-0 p-0">
          {/* ── Header ── */}
          <DialogHeader className="flex-shrink-0 px-5 pt-5 pb-0">
            <div className="min-w-0 pr-6">
              <DialogTitle className="text-base font-bold text-foreground">
                Documentos — {order.orderNumber}
              </DialogTitle>
              <DialogDescription asChild>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{order.supplierName}</span>
                  <span>·</span>
                  <span>{new Date(order.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  <span>·</span>
                  <span className="font-semibold">{curr} {formatMoney(order.total)}</span>
                </div>
              </DialogDescription>
            </div>

            {/* Success bar */}
            {invoiceSuccess && (
              <div className="flex items-center gap-2 mt-3 p-2.5 text-sm font-medium border rounded-lg text-success border-success/20 bg-success/10">
                <Check className="w-4 h-4 flex-shrink-0" />
                {invoiceSuccess}
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 mt-4 border-b border-border">
              <button
                onClick={() => setTab('facturas')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
                  tab === 'facturas'
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                <DollarSign className="w-3.5 h-3.5" />
                Facturas
                {invoices.length > 0 && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                    tab === 'facturas' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {invoices.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTab('remitos')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${
                  tab === 'remitos'
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
                }`}
              >
                <Package className="w-3.5 h-3.5" />
                Remitos
                {remitos.length > 0 && (
                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                    tab === 'remitos' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {remitos.length}
                  </span>
                )}
              </button>
            </div>
          </DialogHeader>

          {/* ── Tab content ── */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">

            {/* ════════════ FACTURAS TAB ════════════ */}
            {tab === 'facturas' && (
              <>
                {/* Action bar */}
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    {invoices.length === 0
                      ? 'Sin facturas registradas para esta OC'
                      : `${invoices.length} factura${invoices.length !== 1 ? 's' : ''} registrada${invoices.length !== 1 ? 's' : ''}`}
                  </p>
                  {!showForm && (
                    <Button
                      size="sm"
                      onClick={() => { setShowForm(true); resetForm(); }}
                      className="h-8 px-3 text-xs bg-primary text-primary-foreground hover:opacity-90"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Nueva Factura
                    </Button>
                  )}
                </div>

                {/* Loading */}
                {loadingInvoices && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {/* Invoice list */}
                {!loadingInvoices && invoices.length > 0 && (
                  <div className="space-y-2">
                    {invoices.map((inv) => {
                      const hasLocalFile = inv.fileUrl?.startsWith('local://');
                      const hasHttpFile = inv.fileUrl?.startsWith('http');
                      const canView = hasLocalFile || hasHttpFile;
                      return (
                        <div key={inv.id} className="overflow-hidden border rounded-xl border-border">
                          {/* Row */}
                          <div className="flex items-center gap-3 px-4 py-3">
                            <button
                              onClick={() => setExpandedId(expandedId === inv.id ? null : inv.id)}
                              className="flex items-center gap-3 flex-1 min-w-0 text-left"
                            >
                              <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-foreground truncate">{inv.invoiceNumber}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(inv.invoiceDate).toLocaleDateString('es-AR')}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <PaymentBadge status={inv.paymentStatus} />
                                <span className="text-sm font-bold text-foreground">
                                  {currSymbol(inv.currency)} {formatMoney(inv.totalAmount)}
                                </span>
                                {expandedId === inv.id
                                  ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  : <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                }
                              </div>
                            </button>
                            {canView && (
                              <button
                                onClick={() => openViewer(
                                  inv.fileUrl!,
                                  inv.invoiceNumber,
                                  hasLocalFile ? 'application/pdf' : 'application/pdf',
                                )}
                                className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                                title="Ver archivo adjunto"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                            {inv.amountPaid === 0 && (
                              deletingId === inv.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" />
                              ) : confirmDeleteId === inv.id ? (
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => handleDeleteInvoice(inv)}
                                    className="p-1.5 rounded-lg bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                                    title="Confirmar eliminación"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground transition-colors"
                                    title="Cancelar"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </span>
                              ) : (
                                <button
                                  onClick={() => setConfirmDeleteId(inv.id)}
                                  className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                                  title="Eliminar factura"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )
                            )}
                          </div>

                          {/* Expanded detail */}
                          {expandedId === inv.id && (
                            <div className="px-4 py-3 border-t border-border bg-muted/20 space-y-3">
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <p className="text-xs text-muted-foreground mb-0.5">Subtotal</p>
                                  <p className="font-medium">{currSymbol(inv.currency)} {formatMoney(inv.subtotal)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-0.5">IVA</p>
                                  <p className="font-medium">{currSymbol(inv.currency)} {formatMoney(inv.ivaTotal)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-0.5">Total</p>
                                  <p className="font-bold">{currSymbol(inv.currency)} {formatMoney(inv.totalAmount)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-0.5">Pagado</p>
                                  <p className="font-medium text-success">{currSymbol(inv.currency)} {formatMoney(inv.amountPaid)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-0.5">Pendiente</p>
                                  <p className={`font-semibold ${inv.pendingAmount > 0 ? 'text-destructive' : 'text-success'}`}>
                                    {currSymbol(inv.currency)} {formatMoney(inv.pendingAmount)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-0.5">Estado pago</p>
                                  <PaymentBadge status={inv.paymentStatus} />
                                </div>
                              </div>
                              {inv.notes && (
                                <div>
                                  <p className="text-xs text-muted-foreground mb-0.5">Notas</p>
                                  <p className="text-sm text-foreground">{inv.notes}</p>
                                </div>
                              )}
                              <div className="flex items-center justify-between pt-1">
                                <p className="text-xs text-muted-foreground">
                                  Registrada: {new Date(inv.createdAt).toLocaleDateString('es-AR')}
                                </p>
                                {inv.fileUrl && !canView && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    Archivo adjunto (sin URL de vista previa)
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Empty state */}
                {!loadingInvoices && invoices.length === 0 && !showForm && (
                  <div className="flex flex-col items-center justify-center py-10 border border-dashed rounded-xl border-border bg-muted/10">
                    <FileText className="w-8 h-8 text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">Sin facturas registradas</p>
                    <p className="text-xs text-muted-foreground/60 mt-0.5">Presioná "Nueva Factura" para registrar una</p>
                  </div>
                )}

                {/* ── New invoice form ── */}
                {showForm && (
                  <div className="border rounded-xl border-border bg-muted/10 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <h4 className="text-sm font-semibold text-foreground">Registrar Factura</h4>
                      <button
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="p-1 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-4 space-y-4">
                      {formErrors.length > 0 && (
                        <div className="flex items-start gap-2 p-3 border rounded-lg border-destructive/20 bg-destructive/10">
                          <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                          <ul className="text-xs text-destructive space-y-0.5">
                            {formErrors.map((e, i) => <li key={i}>• {e}</li>)}
                          </ul>
                        </div>
                      )}

                      <div className="space-y-4">
                        {/* Nº Factura + Moneda */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-foreground sm:text-sm">
                              Nº Factura <span className="text-xs text-red-500">*</span>
                            </Label>
                            <Input
                              value={form.invoiceNumber}
                              onChange={(e) => setForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                              placeholder="FC-A-00154"
                              maxLength={255}
                              className="h-9 text-sm rounded-lg bg-background border-border focus-visible:ring-primary"
                            />
                            <AtLimit value={form.invoiceNumber} max={255} />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-foreground sm:text-sm">
                              Moneda <span className="text-xs text-red-500">*</span>
                            </Label>
                            <Select
                              value={form.currency}
                              onValueChange={(value) => setForm((p) => ({ ...p, currency: value as 'ARS' | 'USD' }))}
                            >
                              <SelectTrigger className="h-9 text-sm rounded-lg bg-background border-border">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ARS">ARS — Peso argentino</SelectItem>
                                <SelectItem value="USD">USD — Dólar</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Fecha */}
                        <InlineDatePicker
                          id="invoiceDate"
                          label={<>Fecha <span className="text-xs text-red-500">*</span></>}
                          value={form.invoiceDate}
                          onChange={(value) => setForm((p) => ({ ...p, invoiceDate: value }))}
                        />

                        {/* Subtotal + IVA */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-foreground sm:text-sm">
                              Subtotal <span className="text-xs text-red-500">*</span>
                            </Label>
                            <MoneyInput
                              value={getNumericDisplayValue(form.subtotal)}
                              currency={form.currency}
                              onValueChange={(value) => setForm((p) => ({ ...p, subtotal: value ?? 0 }))}
                              placeholder="0,00"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-foreground sm:text-sm">IVA</Label>
                            <Select
                              value={form.ivaPercent.toString()}
                              onValueChange={(value) => setForm((p) => ({ ...p, ivaPercent: parseFloat(value) }))}
                            >
                              <SelectTrigger className="h-9 text-sm rounded-lg bg-background border-border">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {IVA_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value.toString()}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Notas */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-foreground sm:text-sm">Notas (opcional)</Label>
                          <Input
                            value={form.notes}
                            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                            placeholder="Observaciones..."
                            maxLength={255}
                            className="h-9 text-sm rounded-lg bg-background border-border focus-visible:ring-primary"
                          />
                          <AtLimit value={form.notes} max={255} />
                        </div>
                      </div>

                      {/* Total display */}
                      {(() => {
                        const ivaTotal = Math.round(form.subtotal * form.ivaPercent / 100 * 100) / 100;
                        const totalAmount = Math.round((form.subtotal + ivaTotal) * 100) / 100;
                        const currPrefix = form.currency === 'ARS' ? '$' : 'US$';
                        return (
                          <div className="space-y-1 px-3 py-2.5 rounded-lg bg-muted/40 border border-border">
                            {form.ivaPercent > 0 && (
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>IVA ({form.ivaPercent}%)</span>
                                <span>{currPrefix} {formatMoney(ivaTotal)}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">Total</span>
                              <span className="text-base font-bold text-foreground">
                                {currPrefix} {formatMoney(totalAmount)}
                              </span>
                            </div>
                            {ocInvoicing && ocInvoicing.sameCurrency && ocInvoicing.ocTotal > 0 && (
                              <div className={`flex items-center justify-between text-xs pt-1 border-t border-border/60 ${ocInvoicing.exceeds ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                                <span>
                                  Facturado: {currPrefix} {formatMoney(ocInvoicing.alreadyInvoiced)} de {currPrefix} {formatMoney(ocInvoicing.ocTotal)}
                                </span>
                                <span>Disponible: {currPrefix} {formatMoney(ocInvoicing.available)}</span>
                              </div>
                            )}
                            {ocInvoicing?.exceeds && (
                              <p className="text-xs text-destructive pt-1">
                                El total supera lo disponible para facturar de esta OC.
                              </p>
                            )}
                          </div>
                        );
                      })()}

                      {/* File upload */}
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">Archivo de factura (opcional)</Label>
                        <DropZone
                          onFileSelected={setInvoiceFile}
                          pending={invoiceFile}
                          onClear={() => setInvoiceFile(null)}
                          label="Arrastrá o hacé clic para adjuntar la factura"
                        />
                      </div>

                      <Button
                        onClick={handleSaveInvoice}
                        disabled={savingInvoice || !!ocInvoicing?.exceeds}
                        className="w-full h-9 text-sm bg-primary text-primary-foreground hover:opacity-90 rounded-lg"
                      >
                        {savingInvoice ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Registrando...</>
                        ) : (
                          <><Check className="w-3.5 h-3.5 mr-2" /> Registrar Factura</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ════════════ REMITOS TAB ════════════ */}
            {tab === 'remitos' && (
              <>
                {loadingRemitos && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {/* Remito list */}
                {!loadingRemitos && remitos.length > 0 && (
                  <div className="space-y-2">
                    {remitos.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 px-4 py-3 border rounded-xl border-border bg-card hover:bg-muted/20 transition-colors">
                        {fileIcon(doc.mimeType)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{doc.fileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(doc.fileSize)} · {new Date(doc.uploadedAt).toLocaleDateString('es-AR')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => openRemitoViewer(doc)}
                            className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                            title="Ver documento"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRemito(doc.id)}
                            className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty state */}
                {!loadingRemitos && remitos.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 border border-dashed rounded-xl border-border bg-muted/10">
                    <Package className="w-8 h-8 text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">Sin remitos cargados</p>
                    <p className="text-xs text-muted-foreground/60 mt-0.5">Subí el archivo del remito abajo</p>
                  </div>
                )}

                {/* Upload zone */}
                <div className="space-y-2">
                  <DropZone
                    onFileSelected={setRemitoFile}
                    pending={remitoFile}
                    onClear={() => setRemitoFile(null)}
                    label="Arrastrá o hacé clic para cargar un remito"
                  />
                  {remitoFile && (
                    <Button
                      onClick={handleSaveRemito}
                      disabled={savingRemito}
                      className="w-full h-9 text-sm bg-primary text-primary-foreground hover:opacity-90 rounded-lg"
                    >
                      {savingRemito ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Guardando...</>
                      ) : (
                        <><Upload className="w-3.5 h-3.5 mr-2" /> Guardar Remito</>
                      )}
                    </Button>
                  )}
                </div>

                <div className="flex items-start gap-2 p-3 border rounded-lg border-border bg-muted/20">
                  <AlertCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Los remitos se almacenan localmente en este navegador. Al configurar S3 se migrarán a la nube automáticamente.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex-shrink-0 flex justify-end px-5 py-4 border-t border-border">
            <Button variant="outline" onClick={handleClose} className="rounded-lg">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer (stacked dialog) */}
      <PdfViewer
        url={viewerUrl}
        title={viewerTitle}
        mimeType={viewerMime}
        onClose={closeViewer}
      />
    </>
  );
};

export default DocumentsDialog;
